import { TubePostDownloadDto } from 'collaboration-service';
import { SelectFileModuleProps } from 'components/FileSelector';
import usePostSelection from 'components/PostSelection/usePostSelection';
import SafeHTML from 'components/SafeHTML/SafeHTML';
import { Button, Icon, Input, Modal, getColor, styled } from 'imaginarity-react-ui';
import * as _ from "lodash";
import * as React from 'react';
import { getTranslated } from 'services/Helpers/TranslationHelpers';
import { useImgI18N } from 'services/ImgI18N';
import { useAppTheme } from 'services/useAppTheme';
import { WikiTheme } from 'views/Wiki/WikiTheme';


interface TubeVideoSelectionProps {
    selectProps: Omit<SelectFileModuleProps, "filesSelected" | "fileSelected">;
    namespace?: string;
    videoSelected?: (video: TubePostDownloadDto) => void;
}

const SelectedItemsContainer = styled.div`
    padding: 10px;
    margin-top: 12px;
    height: auto;
    border: 1px solid ${p => getColor(p, "@middleLightGrey")};
    border-left: 3px solid ${p => getColor(p, "@accent")};
    background-color: ${p => getColor(p, "@veryLightGrey")};
`;
const SelectedItem = styled.div`
    line-height: 32px;
    display: inline-block;
    margin-right: 5px;
    margin-top: 5px;
    padding-left: 10px;
    border: 1px solid ${p => getColor(p, "@middleLightGrey")};
    background: ${p => getColor(p, "@mainBackground")};
    transition: transform 0.2s ease-out;
    box-shadow: 0 0px 8px -6px transparent;
    &:hover{
        color: ${p => getColor(p, "@darkerGrey")};
        border: 1px solid ${p => getColor(p, "@lightGrey")};
        box-shadow: 0 6px 8px -6px ${p => getColor(p, "@lightGrey")};
    }
    svg{
        transition: transform 0.2s ease-out;
        &:hover{
            stroke: ${p => getColor(p, "@accentRed")};
            stroke-width: 3px;
    }
    }
`;
const DeleteIcon = styled.div`
    float: right;
    padding: 0 5px;
    line-height: 32px;
    cursor: pointer;
    transition: transform 0.2s ease-out;
    svg{
        stroke: ${p => getColor(p, "@lightGrey")};
        &:hover{
            stroke: ${p => getColor(p, "@accentRed")};
    }
    }
`;

const SelVidText = styled.span`
    margin-right: 15px;
    font-weight: 900;
`;


const TubeVideoSelection = (p: TubeVideoSelectionProps) => {
    const { videoSelected, namespace, selectProps } = p;
    const { Browse, selectedItems, toggleSelectItem } = usePostSelection<TubePostDownloadDto>({
        namespace: namespace ?? "tube",
        groupTypes: "TUBE",
        postTypes: "TubePost",
        getThumb: (item) => (getTranslated(item.content)?.thumbnails ?? [])[0],
    });

    const [open, setOpen] = React.useState<boolean>(false);
    const { t } = useImgI18N(namespace ?? "general");
    const m = t("select from tube videos");
    const wikiTheme = useAppTheme<WikiTheme>("wikiTheme");

    const selectVideo = React.useMemo(() => (video: TubePostDownloadDto) => () => {
        setOpen(false);
        if (videoSelected)
            videoSelected(video);
    }, [videoSelected]);

    const showFilter = wikiTheme.selectVideo.selectVideoFromTube.showFilter;
    const footer = React.useMemo(() => {
        return (
            <div style={{
                display: "grid",
                gridTemplateColumns: showFilter ? "1fr max-content max-content" : "1fr 1fr",
                columnGap: 5
            }}
            >
                {showFilter &&
                    <div><Input icon='filter' autoFocus placeHolder="search video..." /></div>
                }
                <div><Button {...selectProps.buttonProps} floated='left' icon="times" content={t("cancel")} onClick={() => setOpen(false)} /></div>
                <div><Button {...selectProps.buttonProps} disabled={selectedItems.length === 0} kind="primary" floated='right' icon="save" content={t("use video")} onClick={selectVideo(selectedItems[0] as TubePostDownloadDto)} /></div>
            </div>
        )
    }, [t, selectProps, selectedItems, selectVideo, showFilter])

    return (
        <>
            <Modal
                active={open}
                icon="copy video"
                header={m}
                width="wide"
                height='large'
                fixed
                footer={footer}
            >
                <Browse />
                {selectedItems.length > 0 &&
                    <SelectedItemsContainer>
                        <SelVidText>
                            {t("selected video")}
                        </SelVidText>
                        {_.map(selectedItems, s => {
                            return (
                                <SelectedItem key={s.id}>
                                    <div style={{ width: "100%", display: "grid", gridTemplateColumns: "1fr 30px" }}>
                                        <SafeHTML
                                            allowedTags={[""]}
                                            allowedAttributes={{}}
                                            html={getTranslated((s as any as TubePostDownloadDto).headlines)?.text ?? ""}
                                        />
                                        <DeleteIcon onClick={() => toggleSelectItem((s as any as TubePostDownloadDto))}>
                                            <Icon name="times" style={{ float: "right" }} />
                                        </DeleteIcon>
                                    </div>
                                </SelectedItem>
                            );
                        })
                        }
                    </SelectedItemsContainer>
                }
            </Modal>
            <Button {...selectProps.buttonProps} icon="copy video" onClick={() => setOpen(true)} tooltip={{ tooltipText: m, position: "bottom" }} />
        </>
    );
}
export default TubeVideoSelection;